import React, { useState, useEffect } from "react";
import "../MigrationAnimations.css";

/**
 * Step 6: Import Progress
 * Real-time progress tracking of data import
 */
function Step6Progress({
  migrationData,
  updateMigrationData,
  nextStep,
  type = "business",
}) {
  const [progress, setProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState("");
  const [logs, setLogs] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [goLiveActivated, setGoLiveActivated] = useState(false);

  useEffect(() => {
    startImport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      throw new Error("Authentication token not found");
    }
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  };

  const getApiBaseUrl = () => {
    const baseUrl =
      process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
    return baseUrl;
  };

  const startImport = async () => {
    try {
      // Get entity ID from migrationData
      const entityId =
        type === "business"
          ? migrationData?.businessId
          : migrationData?.staffId;

      if (!entityId) {
        throw new Error(`Missing ${type} ID for import`);
      }

      const baseUrl = getApiBaseUrl();

      // Process each uploaded file
      const uploadedFiles = migrationData?.uploadedFiles || {};
      const dataTypes = ["services", "customers", "staff"]; // Appointments removed
      let allResults = {
        services: { imported: 0, skipped: 0, errors: 0 },
        customers: { imported: 0, skipped: 0, errors: 0 },
        staff: { imported: 0, skipped: 0, errors: 0 },
      };

      for (const dataType of dataTypes) {
        const file = uploadedFiles[dataType];

        if (!file) {
          addLog(`Skipping ${dataType} (no file uploaded)`, "info");
          continue;
        }

        // Step 1: Prepare import (validate and detect conflicts)
        setCurrentTask(`Validating ${dataType}...`);
        addLog(`Starting validation for ${dataType}...`, "info");

        // Create FormData for file upload
        const formData = new FormData();
        formData.append("file", file);
        formData.append("dataType", dataType);
        formData.append(
          "sourcePlatform",
          migrationData?.sourcePlatform || "generic",
        );

        if (migrationData?.fieldMappings?.[dataType]) {
          formData.append(
            "mapping",
            JSON.stringify(migrationData.fieldMappings[dataType]),
          );
        }

        const prepareResponse = await fetch(
          `${baseUrl}/api/import/${type}/${entityId}/prepare-import`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("ogestyle_token")}`,
              // Don't set Content-Type for FormData—browser will set it automatically
            },
            body: formData,
          },
        );

        if (!prepareResponse.ok) {
          let errorMessage = "Unknown error";
          try {
            const errorData = await prepareResponse.json();
            errorMessage =
              errorData.error ||
              errorData.message ||
              `HTTP ${prepareResponse.status}`;
          } catch (parseErr) {
            errorMessage = `HTTP ${prepareResponse.status}: ${prepareResponse.statusText}`;
          }
          throw new Error(`${dataType} validation failed: ${errorMessage}`);
        }

        setProgress(Math.min(20 + dataTypes.indexOf(dataType) * 15, 80));
        addLog(`Validation completed for ${dataType}`, "success");

        const preparedData = await prepareResponse.json();
        const jobId = preparedData.jobId;
        const conflicts = preparedData.conflicts || [];

        // Log conflicts
        if (conflicts.length > 0) {
          addLog(
            `Found ${conflicts.length} potential conflicts in ${dataType}`,
            "warning",
          );
        }

        // Step 2: Execute import
        setCurrentTask(`Importing ${dataType}...`);
        addLog(`Starting data import for ${dataType}...`, "info");

        // Create FormData for execute - needs file again since backend is stateless
        const executeFormData = new FormData();
        executeFormData.append("file", file);
        executeFormData.append("dataType", dataType);
        executeFormData.append(
          "sourcePlatform",
          migrationData?.sourcePlatform || "generic",
        );

        if (migrationData?.fieldMappings?.[dataType]) {
          executeFormData.append(
            "mapping",
            JSON.stringify(migrationData.fieldMappings[dataType]),
          );
        }

        // Get conflict resolutions - Step4 saves as resolvedConflicts
        const conflictResolutions =
          migrationData?.resolvedConflicts ||
          migrationData?.conflictResolutions ||
          {};
        if (Object.keys(conflictResolutions).length > 0) {
          executeFormData.append(
            "conflictResolutions",
            JSON.stringify(conflictResolutions),
          );
        }

        const executeResponse = await fetch(
          `${baseUrl}/api/import/${type}/${entityId}/execute-import`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("ogestyle_token")}`,
              // Don't set Content-Type for FormData
            },
            body: executeFormData,
          },
        );

        if (!executeResponse.ok) {
          const errorData = await executeResponse.json();
          throw new Error(
            `${dataType} import failed: ${errorData.error || errorData.message || "Unknown error"}`,
          );
        }

        const importResult = await executeResponse.json();

        // Accumulate results - backend returns imported/skipped/errors directly
        allResults[dataType] = {
          imported: importResult.imported || 0,
          skipped: importResult.skipped || 0,
          errors: importResult.errors || 0,
        };

        addLog(
          `Completed import for ${dataType}: ${allResults[dataType].imported} imported`,
          allResults[dataType].errors > 0 ? "warning" : "success",
        );
      }

      setProgress(100);
      setResults(allResults);
      updateMigrationData({ importResults: allResults });
      addLog("Import completed successfully!", "success");

      // Save go-live settings if scheduled
      const goLiveEntityId =
        type === "business" ? migrationData.businessId : migrationData.staffId;
      const goLiveEndpoint =
        type === "business"
          ? `${baseUrl}/api/migration/business/${goLiveEntityId}/go-live`
          : `${baseUrl}/api/migration/freelancer/${goLiveEntityId}/go-live`;

      if (migrationData?.goLiveSettings?.goLiveOption === "immediate") {
        try {
          addLog("Activating go-live...", "info");
          const goLiveResponse = await fetch(goLiveEndpoint, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ force: true }),
          });

          if (goLiveResponse.ok) {
            addLog(
              "You're live! Your profile may be hidden until all setup requirements are complete.",
              "success",
            );
            setGoLiveActivated(true);
            updateMigrationData({ goLiveActivated: true });
          } else {
            const errorData = await goLiveResponse.json();
            addLog(
              `Warning: Could not activate go-live: ${errorData.error || "Unknown error"}`,
              "warning",
            );
          }
        } catch (goLiveErr) {
          addLog(
            `Warning: Could not activate go-live: ${goLiveErr.message}`,
            "warning",
          );
        }
      }

      if (migrationData?.goLiveSettings?.goLiveOption === "scheduled") {
        try {
          addLog("Saving go-live schedule...", "info");
          const goLiveResponse = await fetch(goLiveEndpoint, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({
              scheduleGoLive: true,
            }),
          });

          if (goLiveResponse.ok) {
            const goLiveResult = await goLiveResponse.json();
            addLog(`Go-live scheduled: ${goLiveResult.message}`, "success");
            updateMigrationData({ goLiveScheduled: true });
          } else {
            const errorData = await goLiveResponse.json();
            addLog(
              `Warning: Could not schedule go-live: ${errorData.error || "Unknown error"}`,
              "warning",
            );
          }
        } catch (goLiveErr) {
          addLog(
            `Warning: Could not schedule go-live: ${goLiveErr.message}`,
            "warning",
          );
        }
      }

      setIsComplete(true);
    } catch (err) {
      const errorMessage = err.message || "Unknown error during import";
      setError(errorMessage);
      addLog(`Error: ${errorMessage}`, "error");
      setCurrentTask("Import failed");
    }
  };

  const addLog = (message, type = "info") => {
    setLogs((prev) => [
      ...prev,
      {
        message,
        type,
        timestamp: new Date().toLocaleTimeString(),
      },
    ]);
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Step 6: Import Progress</h2>

      {error && (
        <div style={styles.errorBanner}>
          <span>❌ {error}</span>
        </div>
      )}

      {!isComplete ? (
        <>
          <p style={styles.subtitle}>
            Importing your data... Please don't close this window.
          </p>

          {/* Progress Bar */}
          <div style={styles.progressSection}>
            <div style={styles.progressBar}>
              <div
                style={{
                  ...styles.progressFill,
                  width: `${progress}%`,
                }}
              ></div>
            </div>
            <div style={styles.progressText}>{progress}% Complete</div>
          </div>

          {/* Current Task */}
          <div style={styles.currentTask}>
            <div className="spinner" style={styles.spinner}></div>
            <span>{currentTask || "Initializing import..."}</span>
          </div>
        </>
      ) : (
        <>
          <p style={styles.subtitle}>Import completed successfully!</p>

          {/* Live-but-hidden notice for immediate go-live */}
          {goLiveActivated && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                backgroundColor: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: "8px",
                padding: "14px 16px",
                marginBottom: "24px",
              }}
            >
              <span style={{ fontSize: "20px", flexShrink: 0 }}>🟢</span>
              <div>
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#15803d",
                    marginBottom: "4px",
                  }}
                >
                  You're live!
                </div>
                <div style={{ fontSize: "13px", color: "#166534" }}>
                  Your import is complete and your account has been activated.
                  You may appear hidden to customers until all setup requirements
                  are complete — finish your profile and checklist in the
                  dashboard.
                </div>
              </div>
            </div>
          )}

          {/* Results Summary */}
          <div style={styles.resultsGrid}>
            {results &&
              Object.entries(results).map(([dataType, stats]) => {
                // Only show if this is a supported data type
                if (["services", "customers", "staff"].includes(dataType)) {
                  return (
                    <div key={dataType} style={styles.resultCard}>
                      <div style={styles.resultIcon}>
                        {dataType === "services"
                          ? "✂️"
                          : dataType === "customers"
                            ? "👥"
                            : "👤"}
                      </div>
                      <div>
                        <div style={styles.resultType}>
                          {dataType.charAt(0).toUpperCase() + dataType.slice(1)}
                        </div>
                        <div style={styles.resultStats}>
                          <span style={styles.successCount}>
                            ✓ {stats.imported} imported
                          </span>
                          {stats.skipped > 0 && (
                            <span style={styles.warningCount}>
                              ⊘ {stats.skipped} skipped
                            </span>
                          )}
                          {stats.errors > 0 && (
                            <span style={styles.errorCount}>
                              ✕ {stats.errors} errors
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })}
          </div>

          {/* Staff assignment notice */}
          {results?.staff?.imported > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                backgroundColor: "#fffbeb",
                border: "1px solid #fbbf24",
                borderRadius: "8px",
                padding: "14px 16px",
                marginBottom: "24px",
              }}
            >
              <span style={{ fontSize: "20px", flexShrink: 0 }}>📥</span>
              <div>
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#92400e",
                    marginBottom: "4px",
                  }}
                >
                  {results.staff.imported} staff member
                  {results.staff.imported !== 1 ? "s" : ""} imported — location
                  assignment required
                </div>
                <div style={{ fontSize: "13px", color: "#b45309" }}>
                  Imported staff are not yet assigned to a location. Go to{" "}
                  <strong>Staff Management</strong> to assign them so they
                  appear on your team and can accept bookings.
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Activity Log */}
      <div style={styles.logSection}>
        <h3 style={styles.logTitle}>Activity Log</h3>
        <div style={styles.logContainer}>
          {logs.map((log, index) => (
            <div key={index} style={styles.logEntry}>
              <span style={styles.logTime}>{log.timestamp}</span>
              <span
                style={{
                  ...styles.logMessage,
                  color:
                    log.type === "success"
                      ? "#4CAF50"
                      : log.type === "error"
                        ? "#f44336"
                        : log.type === "warning"
                          ? "#ff9800"
                          : "#666",
                }}
              >
                {log.message}
              </span>
            </div>
          ))}
        </div>
      </div>

      {isComplete && (
        <div style={styles.actions}>
          <button onClick={nextStep} style={styles.continueButton}>
            View Summary →
          </button>
        </div>
      )}

      {error && (
        <div style={styles.actions}>
          <button
            onClick={() => window.location.reload()}
            style={styles.retryButton}
          >
            Retry Import
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "32px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  errorBanner: {
    backgroundColor: "#ffebee",
    border: "1px solid #ef5350",
    borderRadius: "8px",
    padding: "12px 16px",
    marginBottom: "24px",
    color: "#c62828",
    fontSize: "14px",
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    marginBottom: "8px",
    color: "#111",
  },
  subtitle: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "32px",
  },
  progressSection: {
    marginBottom: "32px",
  },
  progressBar: {
    width: "100%",
    height: "12px",
    backgroundColor: "#e0e0e0",
    borderRadius: "6px",
    overflow: "hidden",
    marginBottom: "8px",
  },
  progressFill: {
    height: "100%",
    backgroundColor: "#4CAF50",
    transition: "width 0.5s ease",
  },
  progressText: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
  },
  currentTask: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "16px",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    marginBottom: "32px",
    fontSize: "14px",
    color: "#333",
  },
  spinner: {
    width: "20px",
    height: "20px",
    border: "3px solid #e0e0e0",
    borderTop: "3px solid #2196F3",
    borderRadius: "50%",
  },
  resultsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "32px",
  },
  resultCard: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "16px",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  resultIcon: {
    fontSize: "32px",
  },
  resultType: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "4px",
  },
  resultStats: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    fontSize: "12px",
  },
  successCount: {
    color: "#4CAF50",
  },
  warningCount: {
    color: "#ff9800",
  },
  errorCount: {
    color: "#f44336",
  },
  logSection: {
    marginTop: "32px",
  },
  logTitle: {
    fontSize: "16px",
    fontWeight: "600",
    marginBottom: "12px",
    color: "#333",
  },
  logContainer: {
    maxHeight: "200px",
    overflowY: "auto",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    padding: "12px",
    fontSize: "12px",
    fontFamily: "monospace",
  },
  logEntry: {
    display: "flex",
    gap: "12px",
    marginBottom: "6px",
  },
  logTime: {
    color: "#999",
    minWidth: "80px",
  },
  logMessage: {
    color: "#666",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "32px",
  },
  continueButton: {
    padding: "12px 32px",
    backgroundColor: "#4CAF50",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
  },
  retryButton: {
    padding: "12px 32px",
    backgroundColor: "#2196F3",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
  },
};

export default Step6Progress;
